/*
 * THE WORLDS, on the login screen (2026-10-28: "We need a way to select
 * region when logging in."). Midgarth runs more than one world now, one
 * Fly app each, near the players it is for: Sydney's at midgarth.quest
 * and Toronto's at yyz.midgarth.quest. They share nothing. A character
 * made in one is not in the other, nor are its name, password, gear or
 * place on the highscores.
 *
 * Picking a world goes to that world's own site rather than talking to its
 * server from this page. The page, the socket, the highscores and the
 * character pages are then all that world's, same-origin as they always
 * were, and a password manager keeps a login per site, which is a login
 * per world, as the characters are. All this page asks of another world is
 * its /status, for the ping and the count beside its name, which the
 * server lets any site read (handleStatus).
 *
 * The pick is remembered in the site's localStorage, so that somebody who
 * plays in Toronto and types midgarth.quest goes on to Toronto by
 * themselves. Every link to a world carries ?world=<id>, and a page that
 * arrives with it takes it as the pick and stays: that is how a click on
 * Sydney from Toronto gets to Sydney rather than being sent straight back.
 */

import { characterInHash } from "./charlinks";

export interface World {
  id: string;
  /** What the picker calls it: where it is, which is what it is chosen for. */
  name: string;
  /** Where it is played: the site the picker goes to. */
  url: string;
  /** Every host that serves this world, its old and Fly's own included. */
  hosts: string[];
  /**
   * An invite to the game's Discord server that opens on this world's
   * channel (2026-11-02: "use this invite for discord on sydney server
   * ... use this one for toronto"). A new world needs one of its own.
   */
  discord: string;
}

export const WORLDS: World[] = [
  { id: "syd", name: "Sydney", url: "https://midgarth.quest/", hosts: ["midgarth.quest", "tinytibia.anacrolix.link", "tinytibia.fly.dev"], discord: "https://discord.gg/3DxnKdNspG" },
  { id: "yyz", name: "Toronto", url: "https://yyz.midgarth.quest/", hosts: ["yyz.midgarth.quest", "midgarth-yyz.fly.dev"], discord: "https://discord.gg/A7WzpEBVmg" },
];

/** The world served at host, or undefined for one that is none of them (a test server, a laptop). */
export function worldAt(host: string): World | undefined {
  host = host.toLowerCase();
  return WORLDS.find((w) => w.hosts.includes(host));
}

/** The Discord invite for the world served at host: Sydney's for a host that is no world's. */
export function discordInvite(host: string): string {
  return (worldAt(host) ?? WORLDS[0]).discord;
}

/** The address to go to for world w: its site, with the pick on it. */
export function worldHref(w: World): string {
  return `${w.url}?world=${w.id}`;
}

const PICK_KEY = "world";

/**
 * Where the page should be instead, if anywhere, for a page loaded at
 * host with the query search, given the pick stored (or undefined).
 * Returns what to store as the pick, if it changes, and the address to go
 * to, if the page should leave. Kept apart from the page so the tests can
 * run it.
 */
export function worldRoute(host: string, search: string, stored: string | null): { store?: string; go?: string } {
  const here = worldAt(host);
  if (!here) return {};
  const asked = new URLSearchParams(search).get("world");
  if (asked !== null) {
    // Arrived by a world link. The link was for this world (a link for
    // another would have gone there), so this is the pick now; one for a
    // world nobody knows is ignored rather than stored.
    const w = WORLDS.find((x) => x.id === asked);
    return w === here && stored !== here.id ? { store: here.id } : {};
  }
  const picked = WORLDS.find((w) => w.id === stored);
  if (picked && picked !== here) return { go: worldHref(picked) };
  return {};
}

/**
 * Go on to the world picked last time, before anything else on the page
 * happens; and take the ?world= off the address once it is read, so that
 * a bookmark or a reload of it is the plain site. True if the page is
 * leaving.
 */
export function followPickedWorld(): boolean {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(PICK_KEY);
  } catch {
    return false;
  }
  // A link to this world's changelog (#changes, as its Discord posts
  // give) or to one of its characters' pages (charlinks.ts) is about this
  // world: it stays, and the pick is left alone.
  if (location.hash === "#changes" || characterInHash(location.hash) !== null) return false;
  const r = worldRoute(location.host, location.search, stored);
  try {
    if (r.store) localStorage.setItem(PICK_KEY, r.store);
  } catch {
    // Storage refused: the pick is only not remembered.
  }
  if (r.go) {
    location.replace(r.go);
    return true;
  }
  if (new URLSearchParams(location.search).has("world")) {
    const u = new URL(location.href);
    u.searchParams.delete("world");
    history.replaceState(history.state, "", u.pathname + u.search + u.hash);
  }
  return false;
}

/** The colour word for a ping, as the login's own ping and the game's Ping row have it. */
export function pingGrade(ms: number): "good" | "fair" | "bad" {
  return ms < 100 ? "good" : ms < 300 ? "fair" : "bad";
}

/**
 * A world's ping as the login screen shows it (2026-11-01: "Make sure the
 * first ping doesn't include set up, then take the lowest of the last 3").
 *
 * The first status check to a world also looks up its name, opens the
 * connection and agrees the encryption, and on a cold page that is most
 * of the time it takes: a world a 40 ms trip away read 300. So the first
 * check, and the first after one that failed (the connection may be gone
 * with it), is sent twice back to back and only the second is timed; the
 * first has set the connection up for it. Then what is shown is the
 * lowest of the last three trips: a trip can only be slowed by what is in
 * the way, never sped up, so the lowest is the nearest to how far away
 * the world is, and one slow answer does not turn the line red.
 */
export class PingMeter {
  private trips: number[] = [];
  private warm = false;

  constructor(private readonly keep = 3) {}

  /**
   * Before the first timed check, and the first after one that failed:
   * ask url once untimed, read to the end so the connection is free for
   * the timed one. Throws as fetch does.
   */
  async warmUp(url: string | URL, timeout: number): Promise<void> {
    if (this.warm) return;
    await (await fetch(url, { signal: AbortSignal.timeout(timeout), cache: "no-store" })).arrayBuffer();
    this.warm = true;
  }

  /** A check failed: the connection may be gone, and the trips with it. */
  reset() {
    this.warm = false;
    this.trips = [];
  }

  /** Count one trip of ms and say the ping to show: the lowest of the last few. */
  add(ms: number): number {
    this.trips.push(ms);
    if (this.trips.length > this.keep) this.trips.shift();
    return Math.min(...this.trips);
  }
}

/**
 * The row of worlds on the login screen: a button for each, its name over
 * its ping and how many are online. This world's is pressed and goes
 * nowhere; another's goes to its site. This world's numbers come from the
 * page's own status check (see checkServer in main.ts); the others' from
 * a check of their own, run alongside it.
 */
export class WorldPicker {
  private readonly here: World | undefined;
  private readonly lines = new Map<string, HTMLElement>();
  private readonly meters = new Map<string, PingMeter>();

  constructor(
    box: HTMLElement,
    private readonly timeout: number,
  ) {
    this.here = worldAt(location.host);
    for (const w of WORLDS) {
      const b = document.createElement("a");
      b.className = "world";
      b.href = worldHref(w);
      const name = document.createElement("span");
      name.className = "worldname";
      name.textContent = w.name;
      const line = document.createElement("span");
      line.className = "worldline";
      line.textContent = "…";
      b.append(name, line);
      if (w === this.here) {
        b.setAttribute("aria-current", "true");
        b.title = `You are in the ${w.name} world.`;
        b.addEventListener("click", (e) => e.preventDefault());
      } else {
        b.title = `Go to the ${w.name} world, at ${new URL(w.url).host}. Its characters are its own: one made here is not there.`;
      }
      this.lines.set(w.id, line);
      box.append(b);
    }
  }

  /** This world's numbers, off the page's own check. */
  showHere(up: boolean, online?: number, ms?: number) {
    if (this.here) this.show(this.here.id, up, online, ms);
  }

  /** Ask every other world how it is, and show what it says. */
  checkOthers() {
    for (const w of WORLDS) if (w !== this.here) void this.check(w);
  }

  private async check(w: World) {
    try {
      let meter = this.meters.get(w.id);
      if (!meter) this.meters.set(w.id, (meter = new PingMeter()));
      const url = new URL("status", w.url);
      await meter.warmUp(url, this.timeout);
      // Timed to the answer's arrival, before its body is read, as the
      // page's own check is.
      const sent = performance.now();
      const r = await fetch(url, { signal: AbortSignal.timeout(this.timeout), cache: "no-store" });
      const ms = meter.add(Math.round(performance.now() - sent));
      const st = r.ok ? ((await r.json()) as { online?: number }) : null;
      this.show(w.id, st !== null, st?.online, ms);
    } catch {
      this.meters.get(w.id)?.reset();
      this.show(w.id, false);
    }
  }

  private show(id: string, up: boolean, online?: number, ms?: number) {
    const line = this.lines.get(id);
    if (!line) return;
    const parts: [string, string][] = [];
    if (!up) parts.push(["down", "no answer"]);
    else {
      if (typeof ms === "number") parts.push([`ping ${pingGrade(ms)}`, `${ms} ms`]);
      if (typeof online === "number") parts.push(["", `${online} online`]);
    }
    const said = parts.map((p) => p[1]).join(" · ");
    // Written only when it says something new, as the status lines are:
    // see setText in main.ts, and the password managers watching the form.
    if (line.textContent === said) return;
    line.replaceChildren();
    parts.forEach(([cls, text], i) => {
      if (i > 0) line.append(" · ");
      const s = document.createElement("span");
      if (cls) s.className = cls;
      s.textContent = text;
      line.append(s);
    });
  }
}
